import { Button, Container, Heading, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

export interface ErrorFallbackProps {
  error?: Error;
  componentStack?: string;
  eventId?: string;
  resetError?: () => void;
}

/**
 * Error fallback component displayed when an unhandled error occurs
 * Used by Sentry's ErrorBoundary
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Container
      maxW="100vw"
      h="100vh"
      centerContent
      bg={bgColor}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <VStack
        spacing={6}
        p={8}
        borderRadius="lg"
        boxShadow="md"
        bg={cardBgColor}
        borderWidth="1px"
        borderColor={borderColor}
        maxW="md"
        w="full"
        textAlign="center"
      >
        <Heading size="lg">Oops! Something went wrong.</Heading>

        <Text>
          We've been notified about this issue and our team is working on a fix.
        </Text>

        {error && import.meta.env.DEV && (
          <Text fontSize="sm" color="red.500" mt={2}>
            {error.message}
          </Text>
        )}

        <Button
          leftIcon={<FiRefreshCw />}
          colorScheme="blue"
          onClick={() => {
            if (resetError) {
              resetError();
            } else {
              window.location.href = '/';
            }
          }}
        >
          Try Again
        </Button>
      </VStack>
    </Container>
  );
}

export default ErrorFallback;